import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Интерфейсы для клиентского API
export interface ClientCredentials {
  login: string;
  password: string;
}

export interface ClientLead {
  id: number;
  date: string;
  project_id: number;
  project_name: string;
  phone: string;
  note?: string;
  name_note?: string;
  customer_name?: string;
  comment?: string;
  status: string;
  at_work: boolean;
  assigned: boolean;
  happened: boolean;
  invalid_lead: boolean;
  non_call: boolean;
  accept_deal: boolean;
  replaced: boolean;
  created_at: string;
  sent_at?: string;
  bonus: boolean;
  manager_name?: string;
  disput_status?: string;
  disput_comment?: string;
  client_manager_name?: string;
  lead_guid?: string;
  last_comment?: string;
  last_comment_date?: string;
  total_comments?: number;
}

export interface LeadFilters {
  status?: string;
  phone_search?: string;
  call_result_name?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface LeadStatus {
  status: 'unprocessed' | 'at_work' | 'completed' | 'non_call' | 'invalid' | 'postponed';
  comment?: string;
}

export interface BulkStatusUpdate {
  leads: number[];
  status: 'unprocessed' | 'at_work' | 'completed' | 'non_call' | 'invalid' | 'postponed';
}

export interface DisputComment {
  comment: string;
}

export interface DisputStatus {
  status: 'На проверке' | 'Утверждено' | 'Не утверждено';
}

export interface LeadCounts {
  unprocessed: number;
  at_work: number;
  completed: number;
  non_call: number;
  invalid_lead: number;
  postponed: number;
  bonus: number;
}

// Создаем экземпляр axios с базовой конфигурацией
const clientApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена авторизации
clientApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Интерцептор для обработки ошибок
clientApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const clientApiService = {
  // Авторизация клиента
  async clientLogin(credentials: ClientCredentials) {
    const response = await clientApi.post('/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    return response.data;
  },

  // Выход клиента
  async clientLogout() {
    localStorage.removeItem('token');
    return clientApi.post('/client/logout');
  },

  // Получение лидов клиента
  async getClientLeads(filters?: LeadFilters) {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.phone_search) params.append('phone', filters.phone_search);
    if (filters?.call_result_name) params.append('call_result_name', filters.call_result_name);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await clientApi.get(`/leads/client?${params.toString()}`);
    return response.data;
  },

  // Получение дашборда клиента
  async getClientDashboard(filters?: LeadFilters) {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.phone_search) params.append('phone', filters.phone_search);
    if (filters?.call_result_name) params.append('call_result_name', filters.call_result_name);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await clientApi.get(`/leads/client?${params.toString()}`);
    return response.data;
  },

  // Сохранение комментария к лиду
  async saveLeadComment(leadId: number, comment: string) {
    const response = await clientApi.post(`/client/lead/${leadId}/save_comment`, { comment });
    return response.data;
  },

  // Обновление статуса лида
  async updateLeadStatus(leadId: number, statusData: LeadStatus) {
    const response = await clientApi.post(`/client/lead/${leadId}/update_status`, statusData);
    return response.data;
  },

  // Массовое обновление статусов
  async bulkUpdateStatus(bulkData: BulkStatusUpdate) {
    const response = await clientApi.post('/client/bulk_update_status', bulkData);
    return response.data;
  },

  // Сохранение комментария спора
  async saveDisputComment(leadId: number, comment: string) {
    const response = await clientApi.post(`/client/lead/${leadId}/save_disput_comment`, { 
      disput_comment: comment 
    });
    return response.data;
  },

  // Обновление статуса спора
  async updateDisputStatus(leadId: number, status: string) {
    const response = await clientApi.post(`/client/lead/${leadId}/update_disput_status`, { 
      disput_status: status 
    });
    return response.data;
  },

  // Обновление лида клиентом
  async updateByClient(leadId: number, data: {
    disputComment?: string;
    clientManagerName?: string;
    status?: string;
  }) {
    const response = await clientApi.put(`/leads/client/${leadId}`, data);
    return response.data;
  },

  // Сохранение менеджера клиента
  async saveClientManager(leadId: number, managerName: string) {
    const response = await clientApi.post(`/client/lead/${leadId}/save_manager`, { 
      client_manager_name: managerName 
    });
    return response.data;
  },

  // Получение истории споров
  async getDisputHistory(leadId: number) {
    const response = await clientApi.get(`/client/lead/${leadId}/disput-history`);
    return response.data;
  },

  // Получение метрик споров
  async getDisputMetrics() {
    const response = await clientApi.get('/client/disputs/metrics');
    return response.data;
  },

  // Получение лидов со спорами
  async getDisputLeads(status?: string) {
    const params = status ? `?status=${status}` : '';
    const response = await clientApi.get(`/client/disputs/leads${params}`);
    return response.data;
  }
};

export default clientApiService; 